import type { TSlotFormat } from "@libs/figure-spans";
import type {
  TBlockRender,
  TChapterSection,
  TContentBlock,
  TFigureRef,
  THeadlineItem,
  TRenderKind
} from "@/zapi-frontend/shared/types/contentBlocks";

const RENDER_KINDS: TRenderKind[] = ["grid", "records", "barRows", "stackedShare", "series"];

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isRows = (value: unknown): value is string[][] => Array.isArray(value) && value.every(isStringArray);

const asIndex = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : undefined;

const asIndexes = (value: unknown): number[] =>
  Array.isArray(value) ? value.map(asIndex).filter((index): index is number => index !== undefined) : [];

// Pre-v2 chapters put the widget in `kind`, addressed columns by name and copied a band figure's
// value into the block. 232 stored memos are in that shape, so they are rewritten on read rather
// than migrated: nothing above this function needs to know two contracts.
export const fromLegacyBlock = (block: Record<string, unknown>): Record<string, unknown> => {
  const kind = block.kind;

  if (kind === "stats") {
    const keyByItem = new Map<number, string>();
    for (const raw of Array.isArray(block.refs) ? block.refs : []) {
      const ref = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
      const item = asIndex(ref.item);
      if (item !== undefined && typeof ref.key === "string") keyByItem.set(item, ref.key);
    }
    const items = (Array.isArray(block.items) ? block.items : []).map((raw, index) => {
      const item = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
      const key = keyByItem.get(index);
      return { ...item, ...(key ? { key } : {}) };
    });
    return { kind: "headline", id: block.id, label: block.label, items };
  }

  if (kind === "records" || kind === "grid" || kind === "barRows" || kind === "stackedShare" || kind === "series") {
    const cols = isStringArray(block.cols) ? block.cols : [];
    const rows = isRows(block.rows) ? block.rows : [];
    const indexOf = (name: unknown) => (typeof name === "string" ? cols.indexOf(name) : -1);
    const named = (value: unknown) =>
      (isStringArray(value) ? value : []).map(indexOf).filter((index) => index >= 0);
    const render: Record<string, unknown> = { kind, note: block.note };
    if (kind === "records") {
      render.layout = block.layout === "list" ? "list" : "table";
      render.badgeCols = named(block.badgeCols);
    }
    if (kind === "barRows") render.valueCol = indexOf(block.valueCol);
    if (kind === "stackedShare") render.partCols = named(block.partCols);
    if (kind === "series") {
      render.zeroBaseline = (isStringArray(block.zeroBaseline) ? block.zeroBaseline : [])
        .map((name) => rows.findIndex((row) => row[0] === name))
        .filter((index) => index >= 0);
    }
    return { kind: "table", id: block.id, label: block.label, cols, rows, refs: block.refs, render };
  }

  return block;
};

// A widget the data cannot carry is dropped by itself and the table draws in its natural form.
const readRender = (value: unknown, colCount: number): TBlockRender | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const render = value as Record<string, unknown>;
  const kind = RENDER_KINDS.find((candidate) => candidate === render.kind);
  if (!kind) return undefined;
  const note = typeof render.note === "string" ? render.note : null;
  const inRange = (index: number) => index > 0 && index < colCount;
  const base: TBlockRender = { kind, ...(note ? { note } : {}) };

  if (kind === "records") {
    const badgeCols = asIndexes(render.badgeCols).filter(inRange);
    return { ...base, layout: render.layout === "list" ? "list" : "table", ...(badgeCols.length ? { badgeCols } : {}) };
  }
  if (kind === "barRows") {
    const valueCol = asIndex(render.valueCol);
    return valueCol !== undefined && inRange(valueCol) ? { ...base, valueCol } : undefined;
  }
  if (kind === "stackedShare") {
    const partCols = asIndexes(render.partCols).filter(inRange);
    return partCols.length === 2 ? { ...base, partCols: [partCols[0], partCols[1]] } : undefined;
  }
  if (kind === "series") {
    if (colCount < 3) return undefined;
    const zeroBaseline = asIndexes(render.zeroBaseline);
    return { ...base, ...(zeroBaseline.length ? { zeroBaseline } : {}) };
  }
  return base;
};

const readFormat = (value: unknown): TSlotFormat | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const format = value as Record<string, unknown>;
  return typeof format.dp === "number" && typeof format.grouped === "boolean" && typeof format.negative === "boolean"
    ? (format as TSlotFormat)
    : undefined;
};

const readRefs = (value: unknown): TFigureRef[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const refs = value.filter(
    (ref): ref is TFigureRef => !!ref && typeof ref === "object" && typeof (ref as TFigureRef).key === "string"
  );
  return refs.length > 0 ? refs : undefined;
};

// The backend validates blocks before storing them, but the renderer must not trust the memo
// blindly: a hand-edited or migrated row with the wrong shape would crash the chapter view.
export const readContentBlock = (raw: unknown): TContentBlock | null => {
  if (!raw || typeof raw !== "object") return null;
  const block = fromLegacyBlock(raw as Record<string, unknown>);
  const id = typeof block.id === "string" ? block.id : undefined;
  const label = typeof block.label === "string" ? block.label : null;
  const refs = readRefs(block.refs);

  switch (block.kind) {
    case "prose":
      return typeof block.text === "string" && block.text ? { kind: "prose", id, text: block.text, refs } : null;
    case "pullQuote": {
      const text = typeof block.text === "string" && block.text ? block.text : undefined;
      const from = typeof block.from === "string" ? block.from : undefined;
      return text || from ? { kind: "pullQuote", id, label, text, from, refs } : null;
    }
    case "headline": {
      const items = (Array.isArray(block.items) ? block.items : []).flatMap((raw): THeadlineItem[] => {
        const item = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
        const key = typeof item.key === "string" ? item.key : undefined;
        const value = typeof item.value === "string" ? item.value : undefined;
        const format = readFormat(item.format);
        // A tile needs a caption and something to show: its key, or the value it was written with.
        return typeof item.label === "string" && (key || value)
          ? [
              {
                label: item.label,
                key,
                value,
                unit: typeof item.unit === "string" ? item.unit : undefined,
                ...(format ? { format } : {})
              }
            ]
          : [];
      });
      return items.length > 0 ? { kind: "headline", id, label, items } : null;
    }
    case "table":
      return isStringArray(block.cols) && isRows(block.rows) && block.cols.length > 0 && block.rows.length > 0
        ? { kind: "table", id, label, cols: block.cols, rows: block.rows, refs, render: readRender(block.render, block.cols.length) }
        : null;
    case "chain":
      return Array.isArray(block.stages) &&
        block.stages.every(
          (stage) =>
            !!stage &&
            typeof stage === "object" &&
            typeof (stage as Record<string, unknown>).key === "string" &&
            typeof (stage as Record<string, unknown>).label === "string" &&
            typeof (stage as Record<string, unknown>).owner === "string"
        ) &&
        Array.isArray(block.positions) &&
        block.positions.every(
          (position) =>
            !!position &&
            typeof position === "object" &&
            typeof (position as Record<string, unknown>).kind === "string" &&
            typeof (position as Record<string, unknown>).label === "string" &&
            Array.isArray((position as Record<string, unknown>).stages)
        )
        ? ({ ...block, id, label } as TContentBlock)
        : null;
    default:
      return null;
  }
};

export const parseChapterSections = (raw: string): TChapterSection[] => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (section): section is TChapterSection =>
          !!section &&
          typeof section === "object" &&
          typeof (section as Record<string, unknown>).title === "string" &&
          Array.isArray((section as Record<string, unknown>).blocks)
      )
      .map((section) => ({
        ...section,
        blocks: section.blocks.map(readContentBlock).filter((block): block is TContentBlock => block !== null)
      }))
      .filter((section) => section.blocks.length > 0);
  } catch {
    return [];
  }
};

// Chart cells are display strings ("1,180", "38%", "-2"); anything else doesn't chart. A cell
// that does not chart is still stored and still shown in the table.
export const parseNumericCell = (value: string): number | null => {
  const cleaned = value.replace(/[,\s]/g, "").replace(/%$/, "");
  if (!cleaned || !/^[+-]?\d+(\.\d+)?$/.test(cleaned)) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};
